import { useState, useEffect } from "react";
import { useParams } from "wouter";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import type { Performer, JamNight } from "@shared/schema";
import UnifiedParticipantsTable from "@/components/unified-participants-table";
import ConsiderationTable from "@/components/consideration-table";
import SelectedForStageTable from "@/components/selected-for-stage-table";
import UnifiedOnStageTable from "@/components/unified-on-stage-table";
import { useWebSocket } from "@/hooks/use-websocket";

export default function LiveKanban() {
  const { id } = useParams();
  const queryClient = useQueryClient();
  const jamNightId = id ? parseInt(id) : undefined;

  // Fetch jam night data
  const { data: jamNight } = useQuery({
    queryKey: [`/api/jam-nights/${jamNightId}`],
    queryFn: async () => {
      const response = await apiRequest("GET", `/api/jam-nights/${jamNightId}`);
      return response.json() as Promise<JamNight>;
    },
    enabled: !!jamNightId,
  });

  // Fetch performers
  const { data: performers = [], isLoading } = useQuery({
    queryKey: [`/api/performers?jamNightId=${jamNightId}`],
    queryFn: async () => {
      const response = await apiRequest("GET", `/api/performers?jamNightId=${jamNightId}`);
      return response.json() as Promise<Performer[]>;
    },
    enabled: !!jamNightId,
  });

  // Setup WebSocket for real-time updates
  useWebSocket((message) => {
    if (message.type === "participants" && message.jamNightId === jamNightId) {
      queryClient.invalidateQueries({ queryKey: [`/api/performers?jamNightId=${jamNightId}`] });
    }
  });

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-100 via-purple-50 to-indigo-200 p-6">
        <div className="text-center">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-100 via-purple-50 to-indigo-200 relative">
      {/* Background Pattern */}
      <div 
        className="absolute inset-0 opacity-20"
        style={{
          backgroundImage: `radial-gradient(circle at 25% 25%, rgba(255,255,255,0.3) 0%, transparent 50%),
                           radial-gradient(circle at 75% 75%, rgba(59,130,246,0.1) 0%, transparent 50%)`,
        }}
      />

      {/* Header */}
      <div className="relative z-10 px-6 py-4 bg-white/30 backdrop-blur-xl border-b border-white/20">
        <h1 className="text-2xl font-bold text-slate-900/90 drop-shadow-sm">
          Live Kanban - {jamNight?.name || "Jam Night"}
        </h1>
        <p className="text-sm text-slate-600/80 mt-1">
          Drag and drop performers between stages
        </p>
      </div>

      {/* Kanban Board */}
      <div className="relative z-10 p-6">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Column 1: Participants */}
          <div className="lg:col-span-1">
            <div className="sticky top-6">
              <h2 className="text-sm font-semibold text-slate-700/80 uppercase mb-3 tracking-wider">
                Participants Pool
              </h2>
              {/* Force mobile view by wrapping in a container that mimics mobile viewport */}
              <div className="[&_.hidden.lg\\:block]:!hidden [&_.block.lg\\:hidden]:!block">
                <UnifiedParticipantsTable
                  performers={performers}
                  jamNightId={jamNightId}
                  jamNight={jamNight}
                  mode="jam-night"
                />
              </div>
            </div>
          </div>

          {/* Column 2: Under Consideration */}
          <div className="lg:col-span-1">
            <div className="sticky top-6">
              <h2 className="text-sm font-semibold text-slate-700/80 uppercase mb-3 tracking-wider">
                Under Consideration
              </h2>
              {/* Force mobile view */}
              <div className="[&_.hidden.lg\\:block]:!hidden [&_.block.lg\\:hidden]:!block">
                <ConsiderationTable
                  performers={performers}
                  jamNightId={jamNightId}
                />
              </div>
            </div>
          </div>

          {/* Column 3: Selected/Up Next */}
          <div className="lg:col-span-1">
            <div className="sticky top-6">
              <h2 className="text-sm font-semibold text-slate-700/80 uppercase mb-3 tracking-wider">
                Up Next
              </h2>
              {/* Force mobile view */}
              <div className="[&_.hidden.lg\\:block]:!hidden [&_.block.lg\\:hidden]:!block">
                <SelectedForStageTable
                  performers={performers}
                  jamNightId={jamNightId}
                />
              </div>
            </div>
          </div>

          {/* Column 4: On Stage */}
          <div className="lg:col-span-1">
            <div className="sticky top-6">
              <h2 className="text-sm font-semibold text-slate-700/80 uppercase mb-3 tracking-wider">
                On Stage
              </h2>
              {/* Force mobile view */}
              <div className="[&_.hidden.lg\\:block]:!hidden [&_.block.lg\\:hidden]:!block">
                <UnifiedOnStageTable
                  performers={performers}
                  jamNightId={jamNightId}
                  jamNight={jamNight}
                  mode="jam-night"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}